import { useState } from "react";

import { BadgeCheck, Handshake } from "lucide-react";
import type { LucideIcon } from "lucide-react";

import LeadModal from "../landing/LeadModal";

interface Stat {
  num: string;
  label: string;
}

interface Program {
  id: string;
  icon: LucideIcon;
  title: string;
  description: string;
  stats: Stat[];
  ctaLabel: string;
  prefillMessage: string;
}

const PROGRAMS: Program[] = [
  {
    id: "referral",
    icon: Handshake,
    title: "Referral program",
    description:
      "Recommend us to another hotelier. Earn 20% recurring commission on every dollar they spend, for their first 12 months. Pays out monthly, with no cap on referrals and no minimum revenue.",
    stats: [
      { num: "20%", label: "Recurring commission" },
      { num: "12mo", label: "Payout window" },
      { num: "∞", label: "Referral cap" },
    ],
    ctaLabel: "Join the referral program",
    prefillMessage: "I'm interested in joining the referral program.",
  },
  {
    id: "certified",
    icon: BadgeCheck,
    title: "Certified Agency Partner",
    description:
      "Free for Dominant tier subscribers. Get formally trained on the platform, earn the “Certified AI SEO Partner” badge, and get a featured listing in our public agency directory — the same directory hotels search to find help.",
    stats: [
      { num: "$0", label: "Free with Dominant" },
      { num: "3hr", label: "Certification training" },
      { num: "Live", label: "Public directory listing" },
    ],
    ctaLabel: "Apply for certification",
    prefillMessage: "I'd like to apply for the Certified Agency Partner program.",
  },
];

const ProgramCard = ({
  program,
  onCtaClick,
}: {
  program: Program;
  onCtaClick: (program: Program) => void;
}) => {
  const Icon = program.icon;
  return (
    <div className="bg-surface-container-lowest rounded-3xl p-10 shadow-cloud ring-1 ring-outline-variant/10 flex flex-col">
      <span className="w-14 h-14 flex items-center justify-center rounded-2xl bg-surface-container-high text-primary mb-6">
        <Icon className="w-7 h-7" />
      </span>
      <h3 className="font-headline text-2xl font-bold text-on-surface mb-3">{program.title}</h3>
      <p className="text-on-surface-variant leading-relaxed mb-8">{program.description}</p>

      <div className="grid grid-cols-3 gap-4 mb-8">
        {program.stats.map((s) => (
          <div key={s.label} className="text-center">
            <div className="font-headline text-3xl font-extrabold text-primary">{s.num}</div>
            <div className="text-xs text-on-surface-variant mt-1 font-medium">{s.label}</div>
          </div>
        ))}
      </div>

      <div className="mt-auto">
        <button
          onClick={() => onCtaClick(program)}
          className="inline-flex w-full justify-center items-center bg-gradient-to-br from-primary to-primary-container text-on-primary px-6 py-3 rounded-xl font-semibold hover:opacity-90 transition-opacity"
        >
          {program.ctaLabel} →
        </button>
      </div>
    </div>
  );
};

const PartnersPage = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [activeProgram, setActiveProgram] = useState<Program | null>(null);

  const handleCtaClick = (program: Program) => {
    setActiveProgram(program);
    setModalOpen(true);
  };

  return (
    <div className="pt-16 pb-24 px-8">
      <div className="max-w-8xl mx-auto">
        <div className="max-w-3xl mb-16">
          <span className="font-label text-xs font-bold tracking-widest uppercase text-primary">
            Partners
          </span>
          <h1 className="font-headline text-4xl lg:text-6xl font-extrabold text-on-surface mt-4 mb-6 tracking-tight">
            Two ways to grow <span className="text-primary italic">with us.</span>
          </h1>
          <p className="text-on-surface-variant text-xl leading-relaxed">
            Hotels trust other hoteliers. Agencies trust other agencies. We built both programs
            around that simple fact.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {PROGRAMS.map((program) => (
            <ProgramCard key={program.id} program={program} onCtaClick={handleCtaClick} />
          ))}
        </div>
      </div>

      <LeadModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        defaultValues={activeProgram ? { message: activeProgram.prefillMessage } : undefined}
      />
    </div>
  );
};

export default PartnersPage;
